import * as React from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@mui/icons-material/Download';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { downloadExport, getUsers, setUserRole } from '../lib/admin';
import { getUserEmail } from '../lib/authentication';

// Admin: download the Hands-On Time data, and allocate the admin role to other users.
export default function AdminTools() {
    const [downloading, setDownloading] = React.useState('');
    const [message, setMessage] = React.useState(null);
    const [users, setUsers] = React.useState({ loading: true, list: [], error: '' });
    const [updating, setUpdating] = React.useState(0);
    const me = getUserEmail();

    React.useEffect(() => {
        let cancelled = false;
        getUsers().then((response) => {
            if (cancelled) return;
            setUsers(response.ok
                ? { loading: false, list: response.users, error: '' }
                : { loading: false, list: [], error: response.message });
        });
        return () => { cancelled = true; };
    }, []);

    const download = async (kind) => {
        setDownloading(kind);
        setMessage(null);
        const response = await downloadExport(kind);
        setDownloading('');
        if (!response.ok) setMessage({ severity: 'error', text: response.message });
    };

    const changeRole = async (user, role) => {
        setUpdating(user.id);
        setMessage(null);
        const response = await setUserRole(user.id, role);
        setUpdating(0);
        if (response.ok) {
            setUsers((old) => ({ ...old, list: old.list.map((u) => (u.id === user.id ? response.user : u)) }));
            setMessage({
                severity: 'success',
                text: role === 'admin' ? `${user.email} is now an admin.` : `${user.email} is no longer an admin.`,
            });
        } else {
            setMessage({ severity: 'error', text: response.message });
        }
    };

    return (
        <Box>
            <Typography variant="subtitle1">Hands-On Time data</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => download('reports')}
                    disabled={Boolean(downloading)}
                >
                    {downloading === 'reports' ? 'Preparing...' : 'Clock time reports (CSV)'}
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => download('clocks')}
                    disabled={Boolean(downloading)}
                >
                    {downloading === 'clocks' ? 'Preparing...' : 'Requested clocks (CSV)'}
                </Button>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3 }}>Users and roles</Typography>
            <Typography variant="body2" color="text.secondary">
                Admins can review clock requests, download the data and give the admin role to other users.
            </Typography>
            {message ? <Alert severity={message.severity} sx={{ mt: 1 }} onClose={() => setMessage(null)}>{message.text}</Alert> : null}
            {users.loading ? <Box sx={{ textAlign: 'center', p: 2 }}><CircularProgress size={24} /></Box> : null}
            {users.error ? <Alert severity="error" sx={{ mt: 1 }}>{users.error}</Alert> : null}
            <List dense>
                {users.list.map((user) => {
                    const isAdmin = user.role === 'admin';
                    const isMe = user.email === me;
                    return (
                        <ListItem
                            key={user.id}
                            disableGutters
                            secondaryAction={
                                <Button
                                    size="small"
                                    variant={isAdmin ? 'outlined' : 'contained'}
                                    color={isAdmin ? 'error' : 'primary'}
                                    onClick={() => changeRole(user, isAdmin ? 'user' : 'admin')}
                                    disabled={isMe || updating === user.id}
                                    aria-label={`${isAdmin ? 'Remove admin from' : 'Make admin'} ${user.email}`}
                                >
                                    {isAdmin ? 'Remove admin' : 'Make admin'}
                                </Button>
                            }
                        >
                            <ListItemText
                                primary={user.name || user.email}
                                secondary={`${user.email} · ${isAdmin ? 'Admin' : 'User'}${isMe ? ' (you)' : ''}`}
                                sx={{ pr: 14 }}
                            />
                        </ListItem>
                    );
                })}
            </List>
        </Box>
    );
}
